import { getLanguageDefault, getlanguageSupported } from '@/config/languages'
import { segments, definitions } from '@/config/routes'
import { logger } from '@/utils/logger'
import RootLanguageComponent from '@/views/Root.vue'

import type { Component } from 'vue'
import type { RouteRecordRaw, RouteLocationNormalized } from 'vue-router'

import type { Language } from '@/config/languages'

export type TranslatedSegment = Record<Language, string>
export type Segments = Record<string, TranslatedSegment>

const SUFFIX_SEPARATOR = '-'
const DEFAULT_SEGMENT_PARAM = 'segment'

const translatedPathRegex = /{([-a-zA-Z0-9._~!$&\"*+,;=:@]+)}/g

const languageDefault = getLanguageDefault()
const languageSupported = getlanguageSupported()

function getTranslatedValue(
  segments: Segments,
  key: string,
  language: Language
) {
  return segments[key]?.[language] || null
}

/**
 * Parse routes definitions and create routes for each language with translated segments,
 * except for `catch-all` (name) or `root` (meta.root) routes.
 */
function getTranslatedRoutes(
  routesDefinitions: RouteRecordRaw[],
  segments: Segments,
  RootLanguageComponent: Component,
  languages: Language[]
) {
  return languages.map(lang => {
    const children = routesDefinitions
      .filter(route => route.name !== 'catch-all' && route.meta?.root !== true)
      .map(route => {
        const translatedRoute = { ...route }

        // Add language suffix
        if (translatedRoute.name) {
          translatedRoute.name =
            String(translatedRoute.name) + SUFFIX_SEPARATOR + lang
        }

        // Localize segments
        if (translatedRoute.path.match(translatedPathRegex)) {
          const translatedPath = route.path.replace(
            translatedPathRegex,
            (_, segment) => {
              const [key, param] = segment.split(':')
              const translatedValue = getTranslatedValue(segments, key, lang)

              if (translatedValue) {
                return `:${param || DEFAULT_SEGMENT_PARAM}(${translatedValue})`
              }

              logger.warn(
                `No translation found for segment "${key}" in language "${lang}".`
              )

              return segment
            }
          )

          translatedRoute.path = translatedPath
        }

        // No leading slash for children paths
        translatedRoute.path = translatedRoute.path.replace(/^\//, '')

        return translatedRoute
      })

    const root = {
      path: `/:lang(${lang})`,
      component: RootLanguageComponent,
      name: `root-${lang}`,
      // !!!
      meta: {
        // ScrollTop all the time for all routes
        scrollTop: true,
      },
      children,
    }

    return root
  })
}

/**
 * Find `catch-all` (name) route.
 */
function getCatchAllRoute(routesDefinitions: RouteRecordRaw[]) {
  return routesDefinitions.find(route => route.name === 'catch-all')
}

/**
 * Get `root` (meta.root) routes.
 */
function getRootRoutes(routesDefinitions: RouteRecordRaw[]) {
  return routesDefinitions.filter(route => route.meta?.root === true)
}

function getRoutesFromDefinitions(
  routesDefinitions: RouteRecordRaw[],
  segments: Segments,
  RootLanguageComponent: Component,
  languages = languageSupported
) {
  const translatedRoutes = getTranslatedRoutes(
    routesDefinitions,
    segments,
    RootLanguageComponent,
    [...languages]
  )
  const catchAllRoute = getCatchAllRoute(routesDefinitions)
  const rootRoutes = getRootRoutes(routesDefinitions)

  const routes = rootRoutes.concat(translatedRoutes)

  if (catchAllRoute) {
    routes.push(catchAllRoute)
  }

  return routes
}

function getDefinitionByName(name: string) {
  const def = definitions.find(def => def.name === name)

  if (!def) {
    console.warn(`No route definition found for name "${name}".`)

    return null
  }

  return def
}

export const createRoutes = () =>
  getRoutesFromDefinitions(
    definitions,
    segments,
    RootLanguageComponent,
    languageSupported
  )

export const localePathFromName = (name: string, language?: Language) => {
  const def = getDefinitionByName(name)

  if (!def) {
    throw new Error(`No route definition found for name "${name}".`)
  }

  if (def.name === 'catch-all' || def.meta?.root === true) {
    return def.path
  }

  const lang = language || languageDefault
  let { path } = def

  if (path.match(translatedPathRegex)) {
    path = path.replace(translatedPathRegex, (_, segment) => {
      const [key] = segment.split(':')
      const translatedValue = getTranslatedValue(segments, key, lang)

      if (translatedValue) {
        return translatedValue
      }

      console.warn(
        `No translation found for segment "${key}" in language "${lang}".`
      )

      return key
    })
  }

  return `/${lang}${path}`
}

/**
 * Check if a route has a specific name (localized).
 */
export const routeMatchNames = (
  route: RouteLocationNormalized,
  nameOrNames: string | string[]
) => {
  if (!route.name) {
    return false
  }

  const routeName = String(route.name)
  const names = Array.isArray(nameOrNames) ? nameOrNames : [nameOrNames]

  return names.some(name => {
    if (!route.params.lang && routeName === name) {
      return true
    }

    return (
      Boolean(getDefinitionByName(name)) &&
      routeName.startsWith(name + SUFFIX_SEPARATOR)
    )
  })
}
